// libs/db/src/models/digital-coach-chat.ts
import { PromiseExtended, Table } from 'dexie';
import { BaseModelForPush } from './base-model';
import { SyncRecord } from '../types/sync-state';
import { DcChatSessionRecord } from '../types/digital-coach-chat';

// Bump SCHEMA_VERSION whenever any field is added or removed — even non-indexed
// ones — so Dexie runs the upgrade path and old records don't silently return
// undefined for the new field. See offline-sync-safety.md (Dexie Schema rule).
const SCHEMA_VERSION = 1;
const SCHEMA = {
  dcChatSessions: 'id, sessionId, taskId, status, syncStatus',
};

export class DigitalCoachChatDB extends BaseModelForPush {
  dcChatSessions!: Table<DcChatSessionRecord>;

  constructor() {
    super('digitalcoachchatdb');
    this.version(SCHEMA_VERSION).stores(SCHEMA);
    this.dcChatSessions = this.table('dcChatSessions');
    this.setupTableHooks(this.tables);
  }

  clearData() {
    this.tables.forEach((table) => table.clear());
  }

  getSessionByTaskId(
    taskId: string,
  ): PromiseExtended<DcChatSessionRecord | undefined> {
    return this.dcChatSessions.where({ taskId }).first();
  }

  getSessionBySessionId(
    sessionId: string,
  ): PromiseExtended<DcChatSessionRecord | undefined> {
    return this.dcChatSessions.where({ sessionId }).first();
  }

  getAllSessions(): PromiseExtended<DcChatSessionRecord[]> {
    return this.dcChatSessions.toArray();
  }

  /**
   * Read-first upsert (server-seeded pattern, frontend CLAUDE.md gotcha #5):
   * a session may already exist from pull-sync, so .add() would fail. Find by
   * id, .put() the merged row if present, else .add().
   */
  async upsertSession(record: DcChatSessionRecord): Promise<void> {
    const existing = await this.dcChatSessions.get(record.id);
    if (existing) {
      await this.dcChatSessions.put({ ...existing, ...record });
    } else {
      await this.dcChatSessions.add(record);
    }
  }

  // Required by BaseModelForPush but not used: DC chat sessions are pull-only.
  // Sessions are created exclusively via the live chat/start endpoint
  // (startChatSession), never through the generic push-sync endpoint — so there
  // is no unsynced-push path. The model stays on BaseModelForPush (and in
  // readTables only) for consistency with other pull-only models (fln-assessment).
  async getAllUnsyncedRecords(): Promise<Record<string, SyncRecord[]>> {
    return { dcChatSessions: [] };
  }

  // Required by BaseModelForPush but not used (pull-only — see above).
  async updateRecordsSyncStatus(): Promise<number> {
    return 0;
  }
}

export const digitalCoachChatDB = new DigitalCoachChatDB();
